import {
  CommonsUser,
  CommonsUsersFilters,
  CommonsUsersFiltersFunc
} from '../../Commons/CommonsUsers/types'
import { ColumnsType } from 'antd/es/table'
import { Avatar } from '../../../../components/Avatar/Avatar'
import { Sorter } from '../../../../components/Sorter/Sorter'
import { SortOrder } from 'antd/es/table/interface'
import { ability, ACTIONS, ROLES, SUBJECTS } from '../../../Permission'
import { CellWithSubtitle } from '../../../../components/CellWithSubtitle/CellWithSubtitle'
import { Link } from '../../../../components/Link'
import { routes } from '../../../../router'
import { Dropdown } from '../../../../components/Dropdown'
import { MY_COMMUNITY_USER_STATUSES } from '../../constants'
import { ROLES_NAMES } from '../../../../constants'
import {
  Status,
  USER_STATUS_TYPES_TO_CLASSNAME
} from '../../../../components/Status'
import { StartChat } from '../../../../components/StartChat/StartChat'
import { fullFormatPhoneNumber } from '../../../../helper/common'
import { DescriptionCell } from '../../../../components/DescriptionCell/DescriptionCell'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import { UserMenu } from '../../Hospitals/UsersTable/UserMenu'
import { CommonsUsersColumnsFunc } from './types'
import { getContractCategoriesForCommonsTable } from '../../Commons/CommonsUsers/api'

export const columns = ({
  applyFilters,
  filters,
  role,
  onStartChat,
  isNotAvailableForCCS,
  onArchive,
  canEditUser
}: CommonsUsersColumnsFunc): ColumnsType<CommonsUser> => {
  const _columns: ColumnsType<CommonsUser> = [
    {
      dataIndex: 'name',
      key: 'name',
      width: 280,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Name
        </Sorter>
      ),
      render: (_, { first_name, last_name, uuid, avatar_logo }) => {
        const withLink =
          (ability.can(ACTIONS.VIEW, SUBJECTS.COMMONS_USERS) ||
            ability.can(ACTIONS.CRUD, SUBJECTS.COMMONS_USERS)) &&
          !isNotAvailableForCCS
        return (
          <div className="row align-center gap-8">
            <Avatar
              name={`${first_name[0]?.toUpperCase()}${last_name[0]?.toUpperCase()}`}
              src={avatar_logo?.file_preview || ''}
              alt="user image"
            />
            {withLink ? (
              <CellWithSubtitle
                title={
                  <Link
                    to={`${routes.createUserProfile}/${uuid}?fromCommunity=true`}
                  >
                    {first_name} {last_name}
                  </Link>
                }
              />
            ) : (
              `${first_name} ${last_name}`
            )}
          </div>
        )
      },
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 130,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<CommonsUsersFilters, CommonsUsersFiltersFunc, unknown>
          items={MY_COMMUNITY_USER_STATUSES}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status, uuid }) => (
        <div className="row gap-8 align-center">
          <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
            {status}
          </Status.Default>
          {status === 'Active' && (
            <StartChat onClick={() => onStartChat(uuid)} />
          )}
        </div>
      ),
      filterIcon: !!filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'email',
      key: 'email',
      width: 240,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="email"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Email
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      title: 'Phone number',
      width: 196,
      render: (_, { phone_number }) =>
        phone_number ? fullFormatPhoneNumber(phone_number) : '-',
      sorter: false,
      showSorterTooltip: false
    }
  ]
  if (!role) {
    _columns.unshift({
      dataIndex: 'role',
      key: 'role',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CommonsUser>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      render: (text) => ROLES_NAMES[text] ?? <>–</>,
      sorter: true,
      showSorterTooltip: false
    })
  }
  if (role !== ROLES.COMMONS_CLINICAL_COORDINATOR) {
    _columns.push({
      dataIndex: 'contract_categories',
      key: 'contract_categories',
      title: 'Contract categories',
      filterDropdown: (props) => (
        <Dropdown.Filter<CommonsUsersFilters, CommonsUsersFiltersFunc, unknown>
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={getContractCategoriesForCommonsTable}
          field="contract_categories"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { contract_categories }) => {
        const text = contract_categories.map((i) => i.name).join(', ')
        return <DescriptionCell content={text} text={text} />
      },
      filterIcon: !!filters?.contract_categories?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      showSorterTooltip: false
    })
  }
  if (canEditUser) {
    _columns.push({
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: 40,
      fixed: 'right',
      render: (_, row) => {
        const canSeeActions = !('can_be_managed' in row) || row.can_be_managed
        return canSeeActions ? (
          <UserMenu user={row} onArchive={onArchive} />
        ) : null
      }
    })
  }
  return _columns
}
